import { HeaderCancelButtonComponent } from "components/molecules/Header";
import { useFullScreenModal } from "hooks/useFullScreenModal";
import { colors } from "constants/colors";
import { StyledText } from "styles/Text";
import styled from "styled-components";
import { usePostKidsGrowthShareEvent } from "api/queries";
import { useState } from "react";
import RoundRectangleButton from "components/atoms/RoundRectangleButton";
import { toast } from "util/common";
import TextInputAndLabel from "components/molecules/TextInputAndLabel";
import { useAlertStore } from "stores/alertStore";

// 우리 아이 성장 공유 이벤트 신청
const KidsShareSubmit = () => {
  const { requestClose, isInputFocused } = useFullScreenModal();
  const SNS_URL_SLOT_COUNT = 5;

  const [snsUrls, setSnsUrls] = useState<string[]>(
    Array.from({ length: SNS_URL_SLOT_COUNT }, () => ""),
  );

  const handleSnsUrlChange = (index: number) => {
    return (value: string) => {
      setSnsUrls((prev) => {
        const next = [...prev];
        next[index] = value;
        return next;
      });
    };
  };

  const { mutateAsync } = usePostKidsGrowthShareEvent();

  const handleSubmit = () => {
    const trimmed = snsUrls.map((url) => url.trim());

    mutateAsync({
      snsUrl1: trimmed[0],
      snsUrl2: trimmed[1] || undefined,
      snsUrl3: trimmed[2] || undefined,
      snsUrl4: trimmed[3] || undefined,
      snsUrl5: trimmed[4] || undefined,
    })
      .then(() => {
        toast("이벤트 참여가 완료되었습니다.");
        requestClose();
      })
      .catch(() => {
        useAlertStore
          .getState()
          .showAlert({ message: "URL 형식에 맞게 다시 입력해 주세요" });
      });
  };

  return (
    <StyledContainer $isInputFocused={isInputFocused}>
      <HeaderCancelButtonComponent
        goBack={requestClose}
        title="우리 아이 성장 공유 이벤트"
      />
      <StyledTitleSection>
        <StyledContentInner>
          <StyledTitleTextBody16>
            열달 캡처 화면이 담긴 SNS URL을 입력해주세요.
          </StyledTitleTextBody16>
          <StyledText.Sub12 color={colors.darkGray1}>
            *비공개 게시물이나 링크가 확인되지 않은 경우 이벤트 대상자에서
            제외됩니다
          </StyledText.Sub12>
          <StyledText.Sub12 color={colors.darkGray1}>
            <StyledText.Sub12 color={colors.subPurple5}>
              *URL 1개 = 이벤트 참여 1회
            </StyledText.Sub12>
            로 자동 반영됩니다.
          </StyledText.Sub12>
        </StyledContentInner>
      </StyledTitleSection>
      <StyledInputSection>
        <StyledInputScrollWrap>
          <StyledContentInner>
            <StyledInputBox>
              {Array.from({ length: SNS_URL_SLOT_COUNT }, (_, index) => {
                const isFirst = index === 0;
                const label = `URL 0${index + 1} ${isFirst ? "" : "(선택)"}`;
                return (
                  <TextInputAndLabel
                    key={`snsUrl-${index}`}
                    id={`snsUrl-${index}`}
                    label={label}
                    labelSize={13}
                    isRequiredMark={isFirst}
                    placeholder={`SNS URL ${index + 1}`}
                    value={snsUrls[index] ?? ""}
                    onChangeText={handleSnsUrlChange(index)}
                  />
                );
              })}
            </StyledInputBox>
          </StyledContentInner>
        </StyledInputScrollWrap>
      </StyledInputSection>
      <StyledButtonBox>
        <RoundRectangleButton
          text="이벤트 참여하기"
          width="100%"
          isDisabled={!snsUrls[0]?.trim()}
          onClick={handleSubmit}
        />
      </StyledButtonBox>
    </StyledContainer>
  );
};

export default KidsShareSubmit;

/** 키보드 영역만큼 min-height 보강(기기별 편차 있음) */
const KEYBOARD_PAD_EXTRA_MIN_PX = 300;

const StyledContainer = styled.main<{ $isInputFocused: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  min-height: ${({ $isInputFocused }) =>
    $isInputFocused
      ? `calc(100dvh + ${KEYBOARD_PAD_EXTRA_MIN_PX}px)`
      : "100dvh"};
  background-color: ${colors.white};
`;

const StyledTitleSection = styled.section`
  flex-shrink: 0;
  padding: 24px 20px;
`;

const StyledContentInner = styled.div`
  max-width: 360px;
  margin: 0 auto;
`;

const StyledTitleTextBody16 = styled(StyledText.Body16)`
  display: block;
  margin-bottom: 8px;
`;

const StyledInputSection = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 16px;
`;

const StyledInputScrollWrap = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const StyledInputBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledButtonBox = styled.div`
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  padding: 16px 20px 32px;
`;
